import { useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useIsMobile } from '@/hooks/useIsMobile'
import type { StatusCount } from '@/types/api'

const LEAD_FUNNEL_COLORS = {
  all: '#171717',
  contacted: '#737373',
  estimate_scheduled: '#86EFAC',
  estimate_sent: '#4ADE80',
  won: '#22C55E',
}

const PROJECT_FUNNEL_COLORS = {
  all: '#171717',
  estimate_scheduled: '#93C5FD',
  estimate_sent: '#60A5FA',
  won: '#3B82F6',
}

interface SalesFunnelChartProps {
  countByStatus: StatusCount[]
  totalLeads: number
  countByProjectStatus?: StatusCount[]
  totalProjects?: number
  height?: number
}

export function SalesFunnelChart({
  countByStatus,
  totalLeads,
  countByProjectStatus,
  totalProjects,
  height = 300,
}: SalesFunnelChartProps) {
  const isMobile = useIsMobile()
  const funnelData = useMemo(() => {
    // Use project stats if available, otherwise fall back to lead stats
    if (countByProjectStatus && countByProjectStatus.length > 0 && totalProjects && totalProjects > 0) {
      const statusCounts: Record<string, number> = {}
      countByProjectStatus.forEach((item) => {
        statusCounts[item.status] = item.count
      })

      const won = statusCounts['won'] ?? 0
      const estimateSent = (statusCounts['estimate_sent'] ?? 0) + won
      const estimateScheduled = (statusCounts['estimate_scheduled'] ?? 0) + estimateSent
      const allProjects = totalProjects
      const pct = (v: number) => allProjects > 0 ? Math.round((v / allProjects) * 100) : 0

      return [
        { name: 'Won', value: won, percent: pct(won), fill: PROJECT_FUNNEL_COLORS.won },
        { name: 'Estimate Sent', value: estimateSent, percent: pct(estimateSent), fill: PROJECT_FUNNEL_COLORS.estimate_sent },
        { name: 'Estimate Scheduled', value: estimateScheduled, percent: pct(estimateScheduled), fill: PROJECT_FUNNEL_COLORS.estimate_scheduled },
        { name: 'All Projects', value: allProjects, percent: 100, fill: PROJECT_FUNNEL_COLORS.all },
      ]
    }

    // Fallback to lead-based funnel
    const statusCounts: Record<string, number> = {}
    countByStatus.forEach((item) => {
      statusCounts[item.status] = item.count
    })

    const won = statusCounts['won'] ?? 0
    const estimateSent = (statusCounts['estimate_sent'] ?? 0) + won
    const estimateScheduled = (statusCounts['estimate_scheduled'] ?? 0) + estimateSent
    const contacted = (statusCounts['contacted'] ?? 0) + estimateScheduled

    const allLeads = totalLeads
    const pct = (v: number) => allLeads > 0 ? Math.round((v / allLeads) * 100) : 0

    return [
      { name: 'Won', value: won, percent: pct(won), fill: LEAD_FUNNEL_COLORS.won },
      { name: 'Estimate Sent', value: estimateSent, percent: pct(estimateSent), fill: LEAD_FUNNEL_COLORS.estimate_sent },
      { name: 'Estimate Scheduled', value: estimateScheduled, percent: pct(estimateScheduled), fill: LEAD_FUNNEL_COLORS.estimate_scheduled },
      { name: 'Contacted', value: contacted, percent: pct(contacted), fill: LEAD_FUNNEL_COLORS.contacted },
      { name: 'All Leads', value: allLeads, percent: 100, fill: LEAD_FUNNEL_COLORS.all },
    ]
  }, [countByStatus, totalLeads, countByProjectStatus, totalProjects])

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={funnelData}
          layout="vertical"
          margin={{ top: 5, right: isMobile ? 10 : 30, left: isMobile ? 50 : 100, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="percent"
            domain={[0, 100]}
            ticks={[0, 20, 40, 60, 80, 100]}
            tickFormatter={(v: number) => `${v}%`}
          />
          <YAxis dataKey="name" type="category" />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null
              const data = payload[0].payload
              return (
                <div className="rounded border border-border bg-popover text-popover-foreground px-3 py-2 text-sm shadow-sm">
                  <p>total: {data.value}</p>
                  <p>percent: {data.percent}%</p>
                </div>
              )
            }}
          />
          <Bar dataKey="percent" name="Leads" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
